import { useState } from 'react';
import type { ExecutionStatus } from '@/types/statuses.ts';
import {
  DEFAULT_RUN_PROVENANCE_STATES,
  getDefaultRunModes,
  getDefaultRunOrigins,
  getRunOriginFilterOptions,
  DEFAULT_RUN_STATUSES,
  RUN_MODE_FILTER_OPTIONS,
  RUN_PROVENANCE_FILTER_OPTIONS,
  RUN_STATUS_FILTER_OPTIONS,
  RunMode,
  RunProvenanceState,
  RunOrigin,
} from '@/types/runs.ts';

const toggleFilterValue = <T extends string>(values: T[], value: T): T[] => {
  if (!values.includes(value)) {
    return [...values, value];
  }

  if (values.length === 1) {
    return values;
  }

  return values.filter((item) => item !== value);
};

export default function useRunFilters() {
  const [filterStatuses, setFilterStatuses] = useState<ExecutionStatus[]>(() => [
    ...DEFAULT_RUN_STATUSES,
  ]);
  const [filterRunOrigins, setFilterRunOrigins] = useState<RunOrigin[]>(() =>
    getDefaultRunOrigins(),
  );
  const [filterRunModes, setFilterRunModes] = useState<RunMode[]>(() => getDefaultRunModes());
  const [filterProvenanceStates, setFilterProvenanceStates] = useState<RunProvenanceState[]>(
    DEFAULT_RUN_PROVENANCE_STATES,
  );
  const toggleRunMode = (runMode: RunMode) => {
    setFilterRunModes((currentRunModes) => toggleFilterValue(currentRunModes, runMode));
  };

  const toggleStatus = (status: ExecutionStatus) => {
    setFilterStatuses((currentStatuses) => toggleFilterValue(currentStatuses, status));
  };

  const toggleRunOrigin = (runOrigin: RunOrigin) => {
    setFilterRunOrigins((currentRunOrigins) => toggleFilterValue(currentRunOrigins, runOrigin));
  };

  const toggleProvenanceState = (provenanceState: RunProvenanceState) => {
    setFilterProvenanceStates((currentStates) => toggleFilterValue(currentStates, provenanceState));
  };

  const onlyRunOrigin = (runOrigin: RunOrigin) => {
    setFilterRunOrigins([runOrigin]);
  };

  const onlyStatus = (status: ExecutionStatus) => {
    setFilterStatuses([status]);
  };

  const onlyRunMode = (runMode: RunMode) => {
    setFilterRunModes([runMode]);
  };

  const onlyProvenanceState = (provenanceState: RunProvenanceState) => {
    setFilterProvenanceStates([provenanceState]);
  };

  const resetFilters = () => {
    setFilterStatuses([...DEFAULT_RUN_STATUSES]);
    setFilterRunOrigins(getDefaultRunOrigins());
    setFilterRunModes(getDefaultRunModes());
    setFilterProvenanceStates(DEFAULT_RUN_PROVENANCE_STATES);
  };

  const resetStatuses = () => {
    setFilterStatuses([...DEFAULT_RUN_STATUSES]);
  };

  return {
    filterStatuses,
    filterRunOrigins,
    filterRunModes,
    filterProvenanceStates,
    toggleRunMode,
    toggleStatus,
    toggleRunOrigin,
    toggleProvenanceState,
    onlyRunOrigin,
    onlyStatus,
    onlyRunMode,
    onlyProvenanceState,
    resetFilters,
    resetStatuses,
    statusOptions: RUN_STATUS_FILTER_OPTIONS,
    originOptions: getRunOriginFilterOptions(),
    modeOptions: RUN_MODE_FILTER_OPTIONS,
    provenanceOptions: RUN_PROVENANCE_FILTER_OPTIONS,
  };
}
